import React from 'react';
import { WarningOutlined, CheckCircleTwoTone } from '@ant-design/icons';
import { Card, Col, Progress, Row, Space } from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { VehicleServiceKpi } from '../../store/analytics.store';

type ServiceAnalyticsProps = {
  kpi: VehicleServiceKpi | null;
};

/* eslint-disable react/require-default-props */
type BarLabelProps = {
  x?: number;
  y?: number;
  width?: number;
  value?: number;
};

const tooltipContentStyle = {
  borderRadius: 8,
  border: '1px solid #E8EDF2',
  fontSize: 12,
};

const CustomBarLabel = ({ x = 0, y = 0, width = 0, value }: BarLabelProps) => {
  if (!value) return null;
  return (
    <text x={x + width / 2} y={y - 6} fill="#64748B" textAnchor="middle" fontSize={12} fontWeight={600}>
      {value}
    </text>
  );
};

const ServiceAnalytics = ({ kpi }: ServiceAnalyticsProps): JSX.Element => {
  const okCount = kpi?.service_ok_vehicles ?? 0;
  const dueSoonCount = kpi?.service_due_vehicles ?? 0;
  const overdueCount = kpi?.service_overdue_vehicles ?? 0;
  const servicedThisPeriod = okCount;
  const totalVehicles = okCount + dueSoonCount + overdueCount;
  const atRiskRate = totalVehicles > 0 ? ((dueSoonCount + overdueCount) / totalVehicles) * 100 : 0;
  const serviceCompletionRate = totalVehicles > 0 ? (servicedThisPeriod / totalVehicles) * 100 : 0;

  const statusSummaryData = [
    { name: 'OK', count: okCount, fill: '#52c41a' },
    { name: 'Due Soon', count: dueSoonCount, fill: '#faad14' },
    { name: 'Overdue', count: overdueCount, fill: '#f5222d' },
  ];

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={14}>
            <Card className="modern-card h-100" title="Vehicle Service Status" bodyStyle={{ padding: '1.5rem' }}>
              <div className="service-analytics__fleet-label">Fleet Status Overview</div>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={statusSummaryData} margin={{ top: 20, right: 20, left: 0, bottom: 0 }} barSize={48}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748B' }} axisLine={false} tickLine={false} />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#94A3B8' }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <Tooltip
                    cursor={{ fill: '#F8FAFC' }}
                    contentStyle={tooltipContentStyle}
                    formatter={(value: number, _: string, entry: { payload?: { name: string } }) => [
                      `${value} vehicles`,
                      entry.payload?.name ?? '',
                    ]}
                  />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]} label={<CustomBarLabel />}>
                    {statusSummaryData.map((entry) => (
                      <Cell key={entry.name} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>

          <Col xs={24} md={10}>
            <Card className="modern-card h-100" title="Fleet Health Summary" bodyStyle={{ padding: '1.5rem' }}>
              <Row gutter={[0, 24]}>
                <Col span={24}>
                  <div className="service-analytics__metric-row">
                    <Space>
                      <WarningOutlined className="service-analytics__warning-icon" />
                      <span className="service-analytics__metric-label">At Risk Rate</span>
                    </Space>
                    <span className="service-analytics__metric-value service-analytics__metric-value--warning">
                      {atRiskRate.toFixed(1)}%
                    </span>
                  </div>
                  <Progress
                    percent={Number(atRiskRate.toFixed(0))}
                    strokeColor="#faad14"
                    trailColor="#F1F5F9"
                    showInfo={false}
                    size="small"
                  />
                  <div className="service-analytics__metric-desc">
                    {dueSoonCount} due soon · {overdueCount} overdue
                  </div>
                </Col>

                <Col span={24}>
                  <div className="service-analytics__metric-row">
                    <Space>
                      <CheckCircleTwoTone twoToneColor="#1890ff" />
                      <span className="service-analytics__metric-label">Service Completion (This Period)</span>
                    </Space>
                    <span className="service-analytics__metric-value service-analytics__metric-value--info">
                      {serviceCompletionRate.toFixed(1)}%
                    </span>
                  </div>
                  <Progress
                    percent={Number(serviceCompletionRate.toFixed(0))}
                    strokeColor="#1890ff"
                    trailColor="#F1F5F9"
                    showInfo={false}
                    size="small"
                  />
                  <div className="service-analytics__metric-desc">
                    {servicedThisPeriod} of {totalVehicles} vehicles serviced this period
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default ServiceAnalytics;
